import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ApiErrors, AppSessionStorageKeysEnum} from 'types/global';
import {createApiError, isJwtExpired} from 'helpers/global';
import caches from 'helpers/caches';
import {googleLogout} from "@react-oauth/google";

interface AuthState {
    isAuthorized: boolean;
    token: string | null;
    error: string | null;
}

const cached = caches.locale.getItem(AppSessionStorageKeysEnum.User);

const tokenFromCache = cached?.token;
const isTokenValid = typeof tokenFromCache === 'string' && tokenFromCache.length > 0 && !isJwtExpired(tokenFromCache);

if (!isTokenValid && tokenFromCache) {
    caches.locale.removeItem(AppSessionStorageKeysEnum.User);
}

const initialState: AuthState = {
    isAuthorized: isTokenValid,
    token: isTokenValid ? tokenFromCache : null,
    error: null,
};

export const loginThunk = createAsyncThunk<
    string,
    string,
    { rejectValue: ApiErrors }
>(
    'auth/login',
    async (token, thunkAPI) => {
        try {
            return token;
        } catch (e) {
            return thunkAPI.rejectWithValue(createApiError(e));
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            googleLogout();
            state.token = null;
            state.isAuthorized = false;
            caches.locale.removeItem(AppSessionStorageKeysEnum.User);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loginThunk.fulfilled, (state, action: PayloadAction<string>) => {
            state.token = action.payload;
            state.isAuthorized = true;
            state.error = null;

            if (action.payload && action.payload.length > 0) {
                caches.locale.setItem({
                    type: AppSessionStorageKeysEnum.User,
                    data: {
                        token: action.payload,
                        isAuthorized: true,
                    },
                });
            }
        });
        builder.addCase(loginThunk.rejected, (state, action) => {
            state.token = null;
            state.isAuthorized = false;
            state.error = action.payload?.message ?? null;
        });
    },
});

export const {logout} = authSlice.actions;
export default authSlice;
